//Global Variables
import { environment } from 'src/environments/environment';
import { user } from './types/users';

export const bmxurl: string = environment.apitarget;

//global function
export function getAccess(AccessCode: string, user : user) {
    let access: string = "";
    let found: boolean = false;
    if (user) {
        user.accesscontrol.access.forEach(element => {
            if(element.code == AccessCode) {
                if(element.preventionmode == "ACCESS")
                found =  true;
            }
        });

        if(found) {
            found = false;
            return true;
        }
        else {
            return false;
        }
    }
}

//Resource Test and Package Test are for one login only, set per environment in testToolsEmail
export function testToolsAllowed(email: string | null | undefined): boolean {
    return !!email && !!environment.testToolsEmail && email.trim().toLowerCase() == environment.testToolsEmail.toLowerCase();
}

export function ConvertDateFromDB(date: Date): Date {
    let hoursOut: number = Number(new Date(date).getTimezoneOffset());
    return new Date(new Date(date).setHours(new Date(date).getHours() + (hoursOut / 60)));
  }

export function ConvertDateToDB(date: Date): Date {
    let hoursOut: number = Number(new Date(date).getTimezoneOffset());
    return new Date(new Date(date).setHours(new Date(date).getHours() - (hoursOut / 60)));
  }

export function getAdjustedDateWithTimeZone(indate: Date, timeZone: string)
{
    let date = new Date(indate);
    let jsondate = date.toJSON();
    let newjsondate = jsondate + timeZone;
    let newdate = new Date(newjsondate);

    //take into account daylinght savings
    let offset = newdate.getTimezoneOffset();
    newdate.setMinutes(newdate.getMinutes() + offset);
    
    return newdate;
}

//Whether an extra's single date allows it on the given booking date. singledate is held as the
//day written down with a midnight time (wall-clock-as-Z, as venue closings are), so only the date
//part is read and it is compared with the booking date's own calendar day - turning either into
//a Date would let the timezone move it a day. A date that is missing or 01/01/1900 is not set, so
//the extra is allowed on any date.
export function extraAvailableOnDate(extra: { singledate?: string }, date: Date): boolean {
    if (extra == null || date == null) {
        return true;
    }

    const single = extraDateDay(extra.singledate);

    if (single == null) {
        return true;
    }

    const day = date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, "0") + "-" + String(date.getDate()).padStart(2, "0");

    return day == single;
}

//The YYYY-MM-DD part of an extra date, or null when it is not set.
function extraDateDay(value: string): string {
    if (value == null || typeof value != "string" || value.length < 10) {
        return null;
    }

    const day = value.substring(0, 10);

    if (Number(day.substring(0, 4)) <= 1900) {
        return null;
    }

    return day;
}

/*
  A list of resources with each one in it once, in the order first met.

  The "Choose Selectable Resources" tables on the package, upgrade and add-on editors used to
  match their ticks by object rather than by id. A saved package came back as fresh objects, so
  nothing showed as ticked, and ticking the rows again added them to the saved ones instead of
  replacing them. Packages saved that way carry every table twice, and the booking screens
  showed both. This is used on the way in and on the way out so those lists heal themselves.
*/
export function uniqueResources<T>(list: T[]): T[] {
    const seen = new Set<string>();

    return (list ?? []).filter((res: any) => {
        const key = (res?.resourceId ?? res?.resourceid ?? res?.id ?? "").toString();

        if (key == "") { return true; }
        if (seen.has(key)) { return false; }

        seen.add(key);
        return true;
    });
}

//A copy of a list in alphabetical order of name, ignoring case, with numbers in number order so
//"Table 2" comes before "Table 10".
/*
  selectableresourcestrings holds just the ids of the resources ticked on Package Edit, and the details
  come from the group's own resources, so they cannot go out of date. A package not yet saved that
  way still holds whole resources, so both are read.
*/
export function selectableResourceIds(group: any): string[] {
    return (group?.selectableresourcestrings ?? []).map((res: any) => typeof res == "string" ? res : res?.resourceId).filter((id: any) => id != null);
}

/*
  The API holds selectableresourcestrings as resource ids only. Edit Booking keeps the resources
  themselves in it while a booking is being changed, to show and mark them, and they can reach a
  request at any depth - the booking's resources, its copy of the package, an availability request.
  So a request goes as a copy with every one of them turned back into ids, and the screen's own
  data is left as it is.
*/
export function withSelectableIds<T>(payload: T): T {
    const walk = (value: any): any => {
        if (value == null || typeof value !== "object" || value instanceof Date) {
            return value;
        }

        if (Array.isArray(value)) {
            return value.map(walk);
        }

        const copy: any = {};
        Object.keys(value).forEach(key => {
            copy[key] = key == "selectableresourcestrings" && Array.isArray(value[key])
                ? selectableResourceIds({ selectableresourcestrings: value[key] })
                : walk(value[key]);
        });
        return copy;
    };

    return walk(payload);
}

//The group's resources a package offers. ANY and SYSTEMANY are all of them, the rest those ticked.
export function selectedResources(group: any): any[] {
    const all = uniqueResources<any>(group?.resources ?? []);

    if (group?.selectionmode == "ANY" || group?.selectionmode == "SYSTEMANY") {
        return all;
    }

    const ids = selectableResourceIds(group);
    return all.filter(res => ids.includes(res.resourceId));
}

//What a package saves in selectableresourcestrings, or sends with a booking.
export function selectableIdsToSave(group: any): string[] | null {
    if (group?.selectionmode == "ANY" || group?.selectionmode == "SYSTEMANY") {
        return selectedResources(group).map(res => res.resourceId);
    }

    return group?.selectableresourcestrings == null ? null : [...new Set(selectableResourceIds(group))];
}

/*
  Edit Booking works on selectableresourcestrings as the resources themselves, marking the ones booked.
  This fills it in on the package's groups, from the same groups of the package read in full where
  there is one, as a row in the package list may not carry the ticks or the whole group.
*/
export function fillselectableresourcestrings(groups: any[], fullGroups: any[] = null): void {
    (groups ?? []).filter(group => group.mode == "COMPLEX").forEach(group => {
        const full = (fullGroups ?? []).find(item => item.id == group.id) ?? (fullGroups ?? []).find(item => item.resourceId == group.resourceId);
        const ids = selectableResourceIds(full ?? group);

        if ((full?.resources ?? []).length > (group.resources ?? []).length) {
            group.resources = JSON.parse(JSON.stringify(full.resources));
        }

        group.selectableresourcestrings = group.selectionmode == "LIST" || group.selectionmode == "SYSTEMLIST"
            ? uniqueResources<any>(group.resources ?? []).filter(res => ids.includes(res.resourceId))
            : group.resources;
    });
}

export function byName<T>(list: T[]): T[] {
    return [...(list ?? [])].sort((one: any, two: any) =>
        (one?.name ?? "").toString().localeCompare((two?.name ?? "").toString(), undefined, { sensitivity: 'base', numeric: true }));
}
